import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT_LAYOUT, UI_CENTER_X, UI_CENTER_Y } from '../core/Config';
import { pulseVirtualAction } from '../core/VirtualControls';
import { QuestSystem, type QuestEntry } from '../systems/QuestSystem';

export class QuestLogPanel {
  private readonly container: Phaser.GameObjects.Container;
  private readonly rows: Phaser.GameObjects.Rectangle[] = [];
  private readonly labels: Phaser.GameObjects.Text[] = [];
  private readonly detailTitle: Phaser.GameObjects.Text;
  private readonly detailBody: Phaser.GameObjects.Text;
  private readonly emptyText: Phaser.GameObjects.Text;
  private readonly visibleCount: number;
  private entries: QuestEntry[] = [];
  private selectedIndex = 0;
  private windowStart = 0;
  private visibleState = false;

  constructor(private readonly scene: Phaser.Scene) {
    const mobile = IS_PORTRAIT_LAYOUT;
    const panelWidth = mobile ? GAME_WIDTH - 56 : 760;
    const panelHeight = mobile ? 1260 : 720;
    this.visibleCount = mobile ? 6 : 6;
    const rowStartY = mobile ? -420 : -220;
    const rowGap = mobile ? 98 : 58;

    const dim = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x050806, 0.66).setInteractive();
    const shadow = scene.add.rectangle(8, 10, panelWidth, panelHeight, 0x000000, 0.34);
    const outer = scene.add
      .rectangle(0, 0, panelWidth, panelHeight, 0x172019, 0.99)
      .setStrokeStyle(mobile ? 5 : 3, 0xe8c768, 0.96);
    const inner = scene.add
      .rectangle(0, 0, panelWidth - 28, panelHeight - 28, 0x0c120f, 0.99)
      .setStrokeStyle(2, 0x8ebc98, 0.72);
    const title = scene.add.text(0, mobile ? -550 : -316, '퀘스트', {
      fontSize: mobile ? '44px' : '30px',
      color: '#fff0b5',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    const subtitle = scene.add.text(0, mobile ? -494 : -278, mobile ? '진행할 목표를 확인하세요' : 'Q 열기 · 방향키 선택 · Esc 닫기', {
      fontSize: mobile ? '27px' : '17px',
      color: '#b9ddbf',
    }).setOrigin(0.5);
    const closeBg = scene.add
      .rectangle(panelWidth / 2 - (mobile ? 74 : 58), -panelHeight / 2 + (mobile ? 62 : 42), mobile ? 116 : 88, mobile ? 66 : 42, 0x3a4438, 0.98)
      .setStrokeStyle(2, 0xa9c3a4, 0.75)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => pulseVirtualAction('cancel'));
    const closeText = scene.add.text(closeBg.x, closeBg.y, '닫기', {
      fontSize: mobile ? '28px' : '18px',
      color: '#f2f4ed',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    const children: Phaser.GameObjects.GameObject[] = [dim, shadow, outer, inner, title, subtitle, closeBg, closeText];
    for (let slot = 0; slot < this.visibleCount; slot += 1) {
      const y = rowStartY + slot * rowGap;
      const row = scene.add
        .rectangle(0, y, panelWidth - (mobile ? 78 : 72), mobile ? 80 : 46, 0x233129, 0.88)
        .setStrokeStyle(2, 0x6f8d75, 0.55)
        .setInteractive({ useHandCursor: true })
        .on('pointerdown', () => {
          const index = this.windowStart + slot;
          if (!this.entries[index]) return;
          this.selectedIndex = index;
          this.render();
        });
      const label = scene.add.text(-panelWidth / 2 + (mobile ? 76 : 58), y - (mobile ? 23 : 14), '', {
        fontSize: mobile ? '28px' : '19px',
        color: '#eef4ec',
      });
      children.push(row, label);
      this.rows.push(row);
      this.labels.push(label);
    }

    const detailY = mobile ? 312 : 174;
    const detailHeight = mobile ? 350 : 230;
    const detailBg = scene.add
      .rectangle(0, detailY, panelWidth - (mobile ? 78 : 72), detailHeight, 0x202b24, 0.94)
      .setStrokeStyle(2, 0x6f8d75, 0.64);
    this.detailTitle = scene.add.text(-panelWidth / 2 + (mobile ? 76 : 58), detailY - detailHeight / 2 + (mobile ? 28 : 20), '', {
      fontSize: mobile ? '31px' : '21px',
      color: '#ffe391',
      fontStyle: 'bold',
      wordWrap: { width: panelWidth - (mobile ? 152 : 116) },
    });
    this.detailBody = scene.add.text(-panelWidth / 2 + (mobile ? 76 : 58), detailY - detailHeight / 2 + (mobile ? 84 : 60), '', {
      fontSize: mobile ? '25px' : '17px',
      color: '#dfe9dc',
      lineSpacing: mobile ? 10 : 6,
      wordWrap: { width: panelWidth - (mobile ? 152 : 116) },
    });
    this.emptyText = scene.add.text(0, 0, '진행 중인 퀘스트가 없습니다.', {
      fontSize: mobile ? '30px' : '20px',
      color: '#a9c3a4',
    }).setOrigin(0.5);
    children.push(detailBg, this.detailTitle, this.detailBody, this.emptyText);

    this.container = scene.add
      .container(UI_CENTER_X, UI_CENTER_Y, children)
      .setDepth(12150)
      .setScrollFactor(0)
      .setVisible(false);
    this.render();
  }

  toggle() {
    this.visibleState = !this.visibleState;
    this.container.setVisible(this.visibleState);
    this.render();
  }

  hide() {
    this.visibleState = false;
    this.container.setVisible(false);
  }

  isVisible() {
    return this.visibleState;
  }

  move(delta: number) {
    if (!this.visibleState || !this.entries.length) return;
    this.selectedIndex = Phaser.Math.Wrap(this.selectedIndex + delta, 0, this.entries.length);
    this.render();
  }

  confirm() {
    this.render();
  }

  render() {
    this.entries = QuestSystem.getEntries();
    this.selectedIndex = Phaser.Math.Clamp(this.selectedIndex, 0, Math.max(0, this.entries.length - 1));
    const maxStart = Math.max(0, this.entries.length - this.visibleCount);
    this.windowStart = Phaser.Math.Clamp(this.selectedIndex - Math.floor(this.visibleCount / 2), 0, maxStart);

    this.labels.forEach((label, slot) => {
      const index = this.windowStart + slot;
      const entry = this.entries[index];
      const selected = index === this.selectedIndex;
      const status = entry?.progress.status === 'completed'
        ? '완료'
        : `${entry ? entry.progress.currentStepIndex + 1 : 0}/${entry?.definition.steps.length ?? 0}`;
      label
        .setText(entry ? `${selected ? '> ' : '  '}[${status}] ${entry.definition.title}` : '')
        .setColor(selected ? '#ffe391' : entry?.progress.status === 'completed' ? '#9eb5a1' : '#eef4ec')
        .setStyle({ fontStyle: selected ? 'bold' : 'normal' })
        .setVisible(Boolean(entry));
      this.rows[slot]
        .setVisible(Boolean(entry))
        .setFillStyle(selected ? 0x4d684f : 0x233129, selected ? 0.96 : 0.86)
        .setStrokeStyle(2, selected ? 0xe8c768 : 0x6f8d75, selected ? 0.92 : 0.55);
    });

    const selected = this.entries[this.selectedIndex];
    this.emptyText.setVisible(!selected);
    this.detailTitle.setVisible(Boolean(selected));
    this.detailBody.setVisible(Boolean(selected));
    if (!selected) return;

    const category = selected.definition.category === 'main' ? '메인' : '보조';
    const completed = selected.progress.status === 'completed';
    this.detailTitle.setText(`${category} 퀘스트 · ${completed ? '완료' : selected.currentStep?.title ?? '진행 중'}`);
    this.detailBody.setText([
      selected.definition.summary,
      '',
      completed ? '모든 목표를 완료했습니다.' : selected.currentStep?.description ?? '',
    ]);
  }
}
